// https://reactjsexample.com/a-modern-react-implementation-of-authorize-nets-accept-js-library/
// https://github.com/AuthorizeNet/sample-code-node/blob/master/AcceptSuite/create-an-accept-payment-transaction.js
// https://gabrieleromanato.name/nodejs-how-to-implement-payments-with-authorize-net
// https://github.com/brendanbond/react-acceptjs
import React, {useEffect, useState} from 'react';
import ReactDOMServer from 'react-dom/server';
import {useRouter} from "next/router";
import LinearProgress from '@mui/material/LinearProgress';
import {useForm, Controller} from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import {useAcceptJs} from 'react-acceptjs';
import {APIContracts, APIControllers, Constants} from 'authorizenet';
import {composeRangeSliderMarks} from '@/helpers/listing';

const useStyles = makeStyles((theme) => ({
    root: {
      [theme.breakpoints.down('lg')]: {
        margin: '4rem 0 0',
      },
      '& .MuiFormLabel-root, & .MuiFormHelperText-root': {
        color: 'black',
      },
      '& .MuiSlider-markLabel': {
        color: '#444444',
      },
      '& .MuiInput-underline:before': {
        border: 0,
      },
      '& .MuiNativeSelect-icon': {
        display: 'none',
      },
      '& ul[role="listbox"]': {
        margin: '0 !important',
      },
      "& .MuiFormHelperText-contained":{
        fontSize: '14px !important',
        margin: 0,
        padding: 0,
        lineHeight: 1,
      },

      "& .MuiFormControlLabel-root .MuiFormControlLabel-label":{
        margin: '0 0 0 .5rem',
        fontSize: 18
      },

    },
    checkBox: {
      padding: 0,
    },
  }));

const authData = {
  apiLoginID: process.env.NEXT_PUBLIC_AUTHNET_ID,
  apiKey: process.env.NEXT_PUBLIC_AUTHNET_KEY,
  clientKey: process.env.NEXT_PUBLIC_AUTHNET_CLIENT,
};

function AmountSliderLbl(value) {
  return `$ ${value}`;
}
const marks = composeRangeSliderMarks('gift');

const scrollTo = (ref) => {
  if (ref && ref.current /* + other conditions */) {
    ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const GiftCardAuthorizeNetForm = (props) => {
  const classes = useStyles();
  const router = useRouter();

  const {dispatchData, loading, error} = useAcceptJs({
    environment: process.env.NEXT_PUBLIC_AUTHNET_ENV === 'production' ? 'PRODUCTION' : 'SANDBOX',
    authData: authData
  });

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    resetField,
    reset,
    formState: {errors, isSubmitting, isSubmitted}
  } = useForm({
    defaultValues: {
      presentedTo: '',
      amount: '5',
      // shipemail: '',
      sendToRec: false,
      recAddr: '',
      recCity: '',
      recState: '',
      recZip: '',
      fname: '',
      lname: '',
      custAddr: '',
      custCity: '',
      custState: '',
      custZip: '',
      email: '',
      phone: '',
      agree: false,
      cardNumber: '',
      expMonth: '',
      expYear: '',
      cardCode: '',
    }
  });
  const [formSubmitted, setFormSubmitted] = useState(null);
  const watchMailToRec = watch("sendToRec");

  useEffect(() => {
    const subscription = watch((data, {name, type}) => {});
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (form_data) => {
    // Dispatch CC data to Authorize.net and receive payment nonce for use on
    // your server
    const cardData ={
      cardNumber: form_data.cardNumber,
      month: form_data.expMonth,
      year: form_data.expYear,
      cardCode: form_data.cardCode,
    };

    const response = await dispatchData({cardData});

    if (response.messages.resultCode === "Ok") {
      const merchantAuthenticationType = new APIContracts.MerchantAuthenticationType();
      merchantAuthenticationType.setName(authData.apiLoginID);
      merchantAuthenticationType.setTransactionKey(authData.apiKey);

      const paymentType = new APIContracts.PaymentType();
      paymentType.setOpaqueData(response.opaqueData);

      const transactionRequestType = new APIContracts.TransactionRequestType();
      transactionRequestType.setTransactionType(APIContracts.TransactionTypeEnum.AUTHCAPTURETRANSACTION);
      transactionRequestType.setPayment(paymentType);
      transactionRequestType.setAmount(parseFloat(form_data.amount));
      transactionRequestType.setCurrencyCode('USD');
      transactionRequestType.setCustomer({
        "type": "individual",
        "email": form_data.email
      });

      if (form_data.sendToRec) {
        transactionRequestType.setShipTo({
          "firstName": form_data.presentedTo,
          "lastName": "",
          // "company": "Souveniropolis",
          "address": form_data.recAddr,
          "city": form_data.recCity,
          "state": form_data.recState,
          "zip": form_data.recZip,
          "country": "US"
        });
      }
      else {
        const billTo = {
          "firstName": form_data.fname,
          "lastName": form_data.lname,
          // "company": "Souveniropolis",
          "address": form_data.custAddr,
          "city": form_data.custCity,
          "state": form_data.custState,
          "zip": form_data.custZip,
          "country": "US",
        }
        transactionRequestType.setShipTo(billTo);
        transactionRequestType.setBillTo({...billTo, "phoneNumber": form_data.phone, "email": form_data.email});
      }

      const createRequest = new APIContracts.CreateTransactionRequest();
      createRequest.setMerchantAuthentication(merchantAuthenticationType);
      createRequest.setTransactionRequest(transactionRequestType);

      const ctrl = new APIControllers.CreateTransactionController(createRequest?.getJSON());
      // !!! Set to Production. Otherwise, by default it points to Sandbox:
      if (process.env.NEXT_PUBLIC_AUTHNET_ENV === 'production') {
        ctrl.setEnvironment(Constants.endpoint.production);
      }

      ctrl.execute(async () => {
        const apiResponse = ctrl.getResponse();
        // console.log('apiResponse: ', apiResponse);
        if (apiResponse) {
          const responseAuthNet = new APIContracts.CreateTransactionResponse(apiResponse);

          if (responseAuthNet?.getMessages().getResultCode() === APIContracts.MessageTypeEnum.OK) {
            const trans_check = responseAuthNet.getTransactionResponse();

            // trans_check.responseCode == 1 is a successful transaction. Otherwise,
            // there was an issue:
            if (trans_check.responseCode === "1") {
              reset();
              router.push(`/golf/pro-shop/thank-you`);

              let new_msg = {
                msg: [{"text": "Your transaction was successful. Thank you for your payment to Baywood Greens!" +
                    " You will receive an email shortly containing the details of your payment.\n\n" +
                    "The gift card will be mailed to the address chosen during checkout process."}]
              };

              try {
                const email_go = {
                  "gift_card": trans_check.transId || true,
                  "body": ReactDOMServer.renderToString(composeEmailResponse(form_data)),
                  "body_admin": ReactDOMServer.renderToString(composeAdminEmailResponse(form_data)),
                  "subject": 'Gift Card Purchase at Baywoods Golf Shop',
                  "name": 'Baywood Greens',
                  "email": form_data.email,
                  "from": "customerservice@baywoodgreens.com"
                };

                const fetch_api = await fetch("/api/contact", {
                  "method": "POST",
                  "headers": { "content-type": "application/json" },
                  "body": JSON.stringify(email_go),
                });
                const send_mail = await fetch_api.json();
              }
              catch (error) {
                console.error('Gift cards - email sending error: ', error);
              }

              setFormSubmitted(new_msg);
            }
            else if (trans_check.getErrors() !== null) {
              console.warn('responseAuthNet.getTransactionResponse().getErrors', trans_check.getErrors());
              let code = trans_check.getErrors().getError()[0].getErrorCode();
              let text = trans_check.getErrors().getError()[0].getErrorText();
              setFormSubmitted({errors: [{text: "Payment transaction issue: " + text}]});
            }
            else {
              console.warn('ELSE failed', responseAuthNet);
              setFormSubmitted({errors: [{text: 'Transaction failed.'}]});
            }
          }
          else if (responseAuthNet?.getMessages().getResultCode() === APIContracts.MessageTypeEnum.ERROR) {
            console.warn('responseAuthNet.getTransactionResponse', responseAuthNet.getTransactionResponse(), responseAuthNet?.getMessages()?.getMessage());
            if (['E00007'].includes(responseAuthNet?.getMessages()?.getMessage()[0]?.code)) {
              setFormSubmitted({errors: [{text: 'We are experiencing issues with payment processing at the moment. Please, contact us for assistance.'}]});
              console.error('AuthNet failure: ', responseAuthNet?.getMessages()?.getMessage()[0]?.text);
            }
            else {
              setFormSubmitted({errors: responseAuthNet?.getMessages()?.getMessage()});
              console.error('AuthNet failure: ', responseAuthNet?.getMessages()?.getMessage()[0]?.text);
            }
          }
          else {
            setFormSubmitted({errors: [{text: 'We are experiencing issues with payment processing at the moment. Please, contact us for assistance.'}]});
            console.error('AuthNet failure with gift shop');
          }
        }
        else {
          setFormSubmitted({errors: [{text: 'We are experiencing issues with payment processing at the moment. Please, contact us for assistance.'}]});
          console.error('AuthNet failure - no response', apiResponse);
        }
      });
    }
    else {
      console.error('AuthNet failure with gift shop. Response: ', response);
      setFormSubmitted({errors: [{text: response?.messages?.message}]});
    }
  };

  return (
    <Container maxWidth="md">
      <Box key={`gift-card-comp`}>
        <div className={classes.root}>
        {isSubmitted && errors && Object.keys(errors).length > 0 &&
          <Typography color="error" component={`p`}><strong>
            Errors detected with the data entered. Please, verify the values below.
            </strong><br/>
          </Typography>
        }
        {!isSubmitting && formSubmitted &&
        <Box position={`relative`} my={4}>
          {Array.isArray(formSubmitted?.errors) && formSubmitted?.errors?.map((er) => {
              if (er.text) {
                return (
                  <Typography color="error" ref={scrollTo}><strong>{er.text}</strong></Typography>
                )
              }
            })
          }
          {Array.isArray(formSubmitted?.msg) && formSubmitted?.msg?.map((er) => {
            if (er.text) {
              return (
                <Typography color="success" ref={scrollTo}><strong>{er.text}</strong></Typography>
              )
            }
          })
          }
        </Box>
        }
        {isSubmitting &&
        <div><LinearProgress/></div>
        }

        <Box>
          <form noValidate autoComplete="off" key={`gift-cert-form`}
                onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8}>
                <Box mb={1}>
                  <Controller name="presentedTo" control={control}
                              rules={{
                                required: true,
                                pattern: /^[A-Za-z\s\.\-]+$/i
                              }}
                              render={({field}) => (
                                <TextField {...field} id="edit-presentedTo"
                                           label="Presented To"
                                           variant="outlined"
                                           key={`edit-presentedTo`}
                                           inputProps={{maxLength: 255}}
                                           type="text"
                                           placeholder="First Name and Last Name"
                                           fullWidth
                                />
                              )}
                  />
                  {errors?.presentedTo?.type === "required" &&
                  <small className={`form-error`}>This field is
                    required.</small>}
                  {errors?.presentedTo?.type === "pattern" &&
                  <small className={`form-error`}>No special
                    characters/numbers allowed.</small>}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box mb={1}>
                  {/*<Box pl={4}>*/}
                  {/*  <label>In the amount of ($)</label>*/}
                  {/*</Box>*/}
                  <Controller
                    name="amount"
                    control={control}

                    // defaultValue={25}
                    render={({field}) => (
                      <TextField {...field} id="edit-amount"
                                 label="In the amount of ($)"
                                 variant="outlined"
                                 key={`edit-amount`}
                                 inputProps={{maxLength: 10}}
                                 type="number"
                                 valueAsNumber={true}
                                 step={5}
                                 min={5}
                                 // placeholder="$50"
                                 fullWidth
                      />

                      // <Slider {...field} id="edit-amount" key={`edit-amount`}
                      //         onChange={(_, value) => {
                      //           field.onChange(value);
                      //         }}
                      //         getAriaValueText={AmountSliderLbl}
                      //          valueLabelDisplay="auto"
                      //         step={25}
                      //         min={25}
                      //         max={250}
                      //         marks={marks}
                      // />
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={12}>
                <h4 className={`form-helper`}>Gift card will be mailed to Billing address if Recipient Address is not provided</h4>

                <Box  mb={1}>
                  <FormControlLabel control={
                    <Controller name={`sendToRec`} control={control}
                                defaultValue={false}
                                render={({field}) => (
                                  <Checkbox {...field} id="edit-sendToRec"
                                            key={`edit-sendToRec`}
                                            checked={field.value}
                                            className={classes.checkBox}
                                    // onChange={(e) =>
                                    // field.onChange(e.target.checked)}
                                  />
                                )}
                    />
                  }
                    label={`Send this Gift Card to Recipient?`}
                  />

                  {!!watchMailToRec &&
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                      <Box mt={3} mb={1}>
                        <Controller name="recAddr" control={control}
                                    rules={{
                                      required: getValues()['sendToRec'] ? true : null,
                                      pattern: /^[0-9A-Za-z\s\.\-]+$/i
                                    }}
                                    render={({field}) => (
                                      <TextField {...field} id="edit-recAddr"
                                                 label="Recipient's Street Address"
                                                 variant="outlined"
                                                 key={`edit-recAddr`}
                                                 inputProps={{maxLength: 60}}
                                                 type="text"
                                                 placeholder="999 Destination Street, Unit 1"
                                                 fullWidth
                                      />
                                    )}
                        />
                        {errors?.recAddr?.type === "required" &&
                        <small className={`form-error`}>This field is
                          required.</small>}
                        {errors?.recAddr?.type === "pattern" &&
                        <small className={`form-error`}>No special
                          characters allowed.</small>}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box  mb={1}>
                        <Controller name="recCity" control={control}
                                    rules={{
                                      required: getValues()['sendToRec'] ? true : null,
                                      pattern: /^[A-Za-z\s\.\-]+$/i
                                    }}
                                    render={({field}) => (
                                      <TextField {...field} id="edit-recCity"
                                                 label="Recipient's City"
                                                 variant="outlined"
                                                 key={`edit-recCity`}
                                                 inputProps={{maxLength: 40}}
                                                 type="text"
                                                 placeholder="Destination City"
                                                 fullWidth
                                      />
                                    )}
                        />
                        {errors?.recCity?.type === "required" &&
                        <small className={`form-error`}>This field is
                          required.</small>}
                        {errors?.recCity?.type === "pattern" &&
                        <small className={`form-error`}>No special
                          characters/numbers allowed.</small>}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box mb={1}>
                        <Controller name="recState" control={control}
                                    rules={{
                                      required: getValues()['sendToRec'] ? true : null,
                                      pattern: /^[a-zA-Z]{0,2}$/i
                                    }}
                                    render={({field}) => (
                                      <TextField {...field} id="edit-recState"
                                                 label="Recipient's State (code)"
                                                 variant="outlined"
                                                 key={`edit-recState`}
                                                 inputProps={{maxLength: 2}}
                                                 type="text"
                                                 placeholder="DE"
                                                 fullWidth
                                      />
                                    )}
                        />
                        {errors?.recState?.type === "required" &&
                        <small className={`form-error`}>This field is
                          required.</small>}
                        {errors?.recState?.type === "pattern" &&
                        <small className={`form-error`}>Abbreviated state code, e.g.
                          NY.</small>}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box mb={1}>
                        <Controller name="recZip" control={control}
                                    rules={{
                                      required: getValues()['sendToRec'] ? true : null,
                                      pattern: /^[0-9]{0,5}$/i
                                    }}
                                    render={({field}) => (
                                      <TextField {...field} id="edit-recZip"
                                                 label="Recipient's Zip Code"
                                                 variant="outlined"
                                                 key={`edit-recZip`}
                                                 inputProps={{maxLength: 5}}
                                                 type="text"
                                                 placeholder="19971"
                                                 fullWidth
                                      />
                                    )}
                        />
                        {errors?.recZip?.type === "required" &&
                        <small className={`form-error`}>This field is
                          required.</small>}
                        {errors?.recZip?.type === "pattern" &&
                        <small className={`form-error`}>5 digits only.</small>}
                      </Box>
                    </Grid>
                  </Grid>
                  }

                  {/*<Grid item xs={12} md={6}>*/}
                  {/*  <Box mb={1} mt={6}>*/}
                  {/*    <Controller name="shipemail" control={control}*/}
                  {/*                rules={{*/}
                  {/*                  required: true,*/}
                  {/*                  pattern: /^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/i*/}
                  {/*                }}*/}
                  {/*                render={({field}) => (*/}
                  {/*                    <TextField {...field} id="edit-shipemail"*/}
                  {/*                               label="Shipping Email Address"*/}
                  {/*                               variant="outlined"*/}
                  {/*                               key={`edit-shipemail`}*/}
                  {/*                               inputProps={{maxLength: 60}}*/}
                  {/*                               type="text"*/}
                  {/*                               placeholder="shipto_email@domain.com"*/}
                  {/*                               fullWidth*/}
                  {/*                    />*/}
                  {/*                )}*/}
                  {/*    />*/}
                  {/*    <small className={`description`}>Where we should email this card? </small>*/}
                  {/*    {errors?.shipemail?.type === "required" &&*/}
                  {/*    <small className={`form-error`}>This field is*/}
                  {/*      required.</small>}*/}
                  {/*    {errors?.shipemail?.type === "pattern" &&*/}
                  {/*    <small className={`form-error`}>Enter a valid*/}
                  {/*      email.</small>}*/}
                  {/*  </Box>*/}
                  {/*</Grid>*/}

                </Box>
              </Grid>

              <h3>Billing Information</h3>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box mb={1}>
                    <Controller name="fname" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[A-Za-z\s\.\-]+$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-fname"
                                             label="First Name"
                                             variant="outlined"
                                             key={`edit-fname`}
                                             inputProps={{maxLength: 20}}
                                             type="text"
                                             placeholder="Your name"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.fname?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.fname?.type === "pattern" &&
                    <small className={`form-error`}>No special
                      characters/numbers allowed.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={1}>
                    <Controller name="lname" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[A-Za-z\s\.\-]+$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-lname"
                                             label="Last Name"
                                             variant="outlined"
                                             key={`edit-lname`}
                                             inputProps={{maxLength: 30}}
                                             type="text"
                                             placeholder="Your last name"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.lname?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.lname?.type === "pattern" &&
                    <small className={`form-error`}>No special
                      characters/numbers allowed.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box mb={1}>
                    <Controller name="custAddr" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[0-9A-Za-z\s\.\-]+$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-custAddr"
                                             label="Billing Street Address"
                                             variant="outlined"
                                             key={`edit-custAddr`}
                                             inputProps={{maxLength: 60}}
                                             type="text"
                                             placeholder="999 Billing Street, Unit 1"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.custAddr?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.custAddr?.type === "pattern" &&
                    <small className={`form-error`}>No special
                      characters allowed.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box mb={1}>
                    <Controller name="custCity" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[A-Za-z\s\.\-]+$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-custCity"
                                             label="Billing City"
                                             variant="outlined"
                                             key={`edit-custCity`}
                                             inputProps={{maxLength: 40}}
                                             type="text"
                                             placeholder="Billing City"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.custCity?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.custCity?.type === "pattern" &&
                    <small className={`form-error`}>No special
                      characters/numbers allowed.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box mb={1}>
                    <Controller name="custState" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[a-zA-Z]{0,2}$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-custState"
                                             label="Billing State (code)"
                                             variant="outlined"
                                             key={`edit-custState`}
                                             inputProps={{maxLength: 2}}
                                             type="text"
                                             placeholder="DE"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.custState?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.custState?.type === "pattern" &&
                    <small className={`form-error`}>Abbreviated state code, e.g.
                      NY.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box mb={1}>
                    <Controller name="custZip" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[0-9]{0,5}$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-custZip"
                                             label="Billing Zip Code"
                                             variant="outlined"
                                             key={`edit-custZip`}
                                             inputProps={{maxLength: 5}}
                                             type="number"
                                             placeholder="19971"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.custZip?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.custZip?.type === "pattern" &&
                    <small className={`form-error`}>5 digits only.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={1}>
                    <Controller name="email" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-email"
                                             label="Email Address"
                                             variant="outlined"
                                             key={`edit-email`}
                                             inputProps={{maxLength: 60}}
                                             type="text"
                                             placeholder="youremail@domain.com"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.email?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.email?.type === "pattern" &&
                    <small className={`form-error`}>Enter a valid
                      email.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box mb={1}>
                    <Controller name="phone" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[0-9\s\(\)\-ext]+$/i
                                }}
                                render={({field}) => (
                                  <TextField className={classes.phoneField} {...field} id="edit-phone"
                                             label="Phone Number"
                                             variant="outlined"
                                             key={`edit-phone`}
                                             inputProps={{maxLength: 20}}
                                             type="text"
                                             placeholder="(000)000-0000"
                                             fullWidth
                                             helperText="We may call if there are any issues processing payment."
                                  />
                                )}
                    />
                    {errors?.phone?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.phone?.type === "pattern" &&
                    <small className={`form-error`}>Enter a valid phone number,
                      maximum 20 chars.</small>}
                  </Box>
                </Grid>


                <Grid item xs={12} md={6}>
                  <Box mb={1}>
                    <Controller name="cardNumber" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^(?!34)(?!37)[0-9]{16}$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-cardNumber"
                                             label="Card number"
                                             variant="outlined"
                                             key={`edit-cardNumber`}
                                             inputProps={{maxLength: 16}}
                                             type="text"
                                             placeholder="xxxxxxxxxxxxxxxx"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.cardNumber?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.cardNumber?.type === "pattern" &&
                    <small className={`form-error`}>Enter a valid credit card
                      number. Amex cards are not accepted.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box mb={1}>
                    <Controller name="expMonth" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^(0[1-9]|1[0-2])$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-expMonth"
                                             label="Exp. month"
                                             variant="outlined"
                                             key={`edit-expMonth`}
                                             inputProps={{maxLength: 2}}
                                             type="text"
                                             placeholder="05"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.expMonth?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.expMonth?.type === "pattern" &&
                    <small className={`form-error`}>Month in a 2-digit
                      format.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box mb={1}>
                    <Controller name="expYear" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^(2[0-9][0-9][0-9])$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-expYear"
                                             label="Exp. year"
                                             variant="outlined"
                                             key={`edit-expYear`}
                                             inputProps={{maxLength: 4}}
                                             type="text"
                                             placeholder="2022"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.expYear?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.expYear?.type === "pattern" &&
                    <small className={`form-error`}>Year in a 4-digit
                      format.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box mb={1}>
                    <Controller name="cardCode" control={control}
                                rules={{
                                  required: true,
                                  pattern: /^[0-9]{3,4}$/i
                                }}
                                render={({field}) => (
                                  <TextField {...field} id="edit-cardCode"
                                             label="CVV"
                                             variant="outlined"
                                             key={`edit-cardCode`}
                                             inputProps={{maxLength: 4}}
                                             type="text"
                                             placeholder="999"
                                             fullWidth
                                  />
                                )}
                    />
                    {errors?.cardCode?.type === "required" &&
                    <small className={`form-error`}>This field is
                      required.</small>}
                    {errors?.cardCode?.type === "pattern" &&
                    <small className={`form-error`}>3 or 4 digit code on the back
                      of your card.</small>}
                  </Box>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box mb={1}>
                    <FormControlLabel control={
                      <Controller name={`agree`} control={control}
                                  defaultValue={false}
                                  rules={{
                                    required: true,
                                  }}
                                  render={({field}) => (
                                    <Checkbox {...field} id="edit-agree"
                                              key={`edit-agree`}
                                              checked={field.value}
                                      // onChange={(e) =>
                                      // field.onChange(e.target.checked)}
                                    />
                                  )}
                      />
                    }
                                      label={`I authorize Baywood Greens to charge the credit card indicated above, and hereby agree to the terms & conditions, privacy, security and refund policy.`}
                    />
                    {errors?.agree?.type === "required" &&
                    <small className={`form-error`}>Your consent is required to proceed.</small>}
                  </Box>
                </Grid>


                <Grid item xs={12} md={12}>
                  <Box align="right" mb={5}>
                      <Button key={`edit-gift-submit`} color="primary"
                              name="gift-submit" type="submit"
                               disableElevation variant="contained"
                      >Send Payment</Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Box>
      </div>
      </Box>
    </Container>
  );
};

const composeEmailResponse = (data) => {
  let res = (
    <Box>
      <h1>Thank you for purchasing a gift card with us!</h1>
      <p>You will find details of your order below.</p>
      <br /><strong>Gift Card amount: </strong>${data.amount} <br /><br />
      <strong>Gift Card Presented To: </strong>{data.presentedTo} <br />

      {/*<h4>We will email the gift card to this address:  {data.shipemail}</h4>*/}
      <br />

      {!data.sendToRec &&
      <>
        <h4>The card will be mailed to your Billing Address provided during checkout: </h4>
        <br />
      </>
      }
      {data.sendToRec &&
      <>
        <h4>Billing Address</h4>
        <br />
      </>
      }
      <strong>Full Name: </strong>{data.fname} {data.lname} <br />
      <strong>Email Address: </strong>{data.email} <br />
      <strong>Phone Number: </strong>{data.phone} <br /><br />
      <strong>Address: </strong> <br />{data.custAddr} <br /> {data.custCity}, {data.custState} {data.custZip} <br /><br />
      <strong>Credit Card Number: </strong>XXXX-XXXX-XXXX-{data.cardNumber?.slice(-4)}<br />
      <strong>Expiration Date: </strong>{data.expMonth} / {data.expYear}
      <br /><br />
      {data.sendToRec && data.recAddr &&
        <>
          <h4>The card will be mailed to the Gift Recipient's address provided during checkout: </h4>
          <br /><br />
          <strong>Name:</strong>{data.presentedTo} <br />
          <strong>Address:</strong> <br /> {data.recAddr} <br /> {data.recCity}, {data.recState} {data.recZip} <br /><br />
        </>
      }
      <br /><br />-----------------------<br /><br />
      <h4>If you have any questions or concerns about this payment, please contact us at (302)-947-9800.</h4>
    </Box>
  );

  return res;
}

const composeAdminEmailResponse = (data) => {
  let res = (
      <Box>
        <h1>Find details of this order below.</h1>
        <br /><strong>Gift Card amount: </strong>${data.amount} <br /><br />
        <strong>Gift Card Presented To: </strong>{data.presentedTo} <br />

        {/*<h4>The gift card should be e-mailed to this address:  {data.shipemail}</h4>*/}
        <br />

        {!data.sendToRec &&
        <>
          <h4>The card should be mailed to the Billing Address provided during checkout: </h4>
          <br />
        </>
        }
        {data.sendToRec &&
        <>
          <h4>Billing Address</h4>
          <br />
        </>
        }
        <strong>Full Name: </strong>{data.fname} {data.lname} <br />
        <strong>Email Address: </strong>{data.email} <br />
        <strong>Phone Number: </strong>{data.phone} <br /><br />
        <strong>Address: </strong> <br />{data.custAddr} <br /> {data.custCity}, {data.custState} {data.custZip} <br /><br />
        <strong>Credit Card Number: </strong>XXXX-XXXX-XXXX-{data.cardNumber?.slice(-4)}<br />
        <strong>Expiration Date: </strong>{data.expMonth} / {data.expYear}
        <br /><br />
        {data.sendToRec && data.recAddr &&
        <>
          <h4>The card should be mailed to the Gift Recipient's address provided during checkout: </h4>
          <br /><br />
          <strong>Name:</strong>{data.presentedTo} <br />
          <strong>Address:</strong> <br /> {data.recAddr} <br /> {data.recCity}, {data.recState} {data.recZip} <br /><br />
        </>
        }
      </Box>
  );

  return res;
}

export default GiftCardAuthorizeNetForm;
